import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { QueryBoundary } from '@/components/query-boundary';
import { KpiStrip } from '@/components/kpi-strip';
import { EmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import { useMessages, useMarkRead, useDeleteMessage } from '@/lib/queries/messages';
import { summarizeMessages } from '@/lib/admin-summaries';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, Mail, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '@/components/confirm';
import { normalizeMessagesSearch, type MessagesFilter } from '@/lib/route-search';

export const Route = createFileRoute('/messages')({
  validateSearch: normalizeMessagesSearch,
  component: MessagesPage,
});

function MessagesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/messages' });
  const { page, filter } = search;
  const [openId, setOpenId] = useState<string | null>(null);
  const q = useMessages(page, 20, filter);
  const markRead = useMarkRead();
  const del = useDeleteMessage();
  const confirm = useConfirm();

  function changeFilter(next: MessagesFilter) {
    if (next === filter) return;
    setOpenId(null);
    void navigate({ search: { filter: next, page: 1 } });
  }

  function changePage(next: number) {
    setOpenId(null);
    void navigate({ search: previous => normalizeMessagesSearch({ ...previous, page: next }) });
  }

  async function onDelete(id: string) {
    const ok = await confirm({
      title: '메시지 삭제',
      description: '삭제한 메시지는 휴지통에서 복구할 수 있습니다.',
      confirmLabel: '삭제',
      tone: 'destructive',
    });
    if (!ok) return;
    try { await del.mutateAsync(id); toast.success('삭제됨'); }
    catch (e) { toast.error((e as Error).message); }
  }

  return (
    <>
      <PageHeader title="메시지" description="방문자 문의" />

      <div className="inline-flex min-h-9 items-center rounded-md border border-border bg-card p-0.5 mb-4 flex-wrap gap-y-0.5 max-w-full">
        <FilterChip active={filter === 'all'} onClick={() => changeFilter('all')}>전체</FilterChip>
        <FilterChip active={filter === 'unread'} onClick={() => changeFilter('unread')}>안 읽음</FilterChip>
        <FilterChip active={filter === 'read'} onClick={() => changeFilter('read')}>읽음</FilterChip>
      </div>

      <QueryBoundary
        query={q}
      >
        {data => {
          const summary = summarizeMessages(data);

          return (
          <>
            <KpiStrip
              items={[
                { label: '조회 결과', value: summary.total.toLocaleString('ko-KR') },
                { label: '안 읽음', value: summary.unreadOnPage.toLocaleString('ko-KR'), tone: summary.unreadOnPage > 0 ? 'warn' : 'default', hint: '현재 페이지' },
                { label: '읽음', value: summary.readOnPage.toLocaleString('ko-KR'), tone: 'sage', hint: '현재 페이지' },
                { label: '최근 수신', value: formatDate(summary.latestDate), hint: currentFilterLabel(filter) },
              ]}
            />

            {data.data.length === 0 ? (
              <EmptyState
                title={filter === 'unread' ? '안 읽은 메시지가 없습니다.' : filter === 'read' ? '읽은 메시지가 없습니다.' : '메시지가 없습니다.'}
                description={filter === 'all' ? '방문자 문의가 들어오면 이곳에 표시됩니다.' : '다른 필터에서 메시지를 확인할 수 있습니다.'}
                compact
              />
            ) : (
              <div className="rounded-[var(--radius)] border border-border bg-card overflow-hidden shadow-xs">
                <ul className="divide-y divide-border">
                  {data.data.map(m => {
                  const isOpen = openId === m.id;
                  return (
                    <li key={m.id} className="group">
                      <button
                        type="button"
                        className="w-full text-left flex items-start gap-3 px-5 sm:px-6 py-3.5 hover:bg-accent/40 transition-colors"
                        onClick={() => {
                          setOpenId(isOpen ? null : m.id);
                          if (!m.is_read) markRead.mutate({ id: m.id, is_read: true });
                        }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`text-[14px] truncate tracking-tight ${m.is_read ? 'font-medium' : 'font-semibold'}`}>
                              {m.name || 'Anonymous'}
                            </span>
                            {!m.is_read && <Badge variant="unread" className="shrink-0">new</Badge>}
                            <span className="text-[12px] text-muted-foreground/70 truncate hidden sm:inline">
                              {m.email}
                            </span>
                          </div>
                          <p className="mt-1 text-[12.5px] text-muted-foreground line-clamp-1">
                            {m.message}
                          </p>
                        </div>

                        <time className="font-mono text-[11.5px] text-muted-foreground tabular-nums shrink-0 hidden sm:block">
                          {new Date(m.created_at).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' })}
                        </time>

                        <ChevronDown
                          className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                          strokeWidth={1.75}
                        />
                      </button>

                      {isOpen && (
                        <div className="px-4 sm:px-5 pb-4 pt-1 bg-muted/20 border-t border-border">
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-muted-foreground mb-3">
                            <span>{m.email}</span>
                            <span>·</span>
                            <span className="font-mono tabular-nums">
                              {new Date(m.created_at).toLocaleString('ko-KR')}
                            </span>
                          </div>
                          <div className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-foreground">
                            {m.message}
                          </div>
                          <div className="mt-4 flex gap-2">
                            <a href={`mailto:${m.email}`}>
                              <Button variant="outline" size="sm"><Mail /> 답장</Button>
                            </a>
                            <Button
                              variant="ghost" size="sm"
                              className="hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => onDelete(m.id)}
                            >
                              <Trash2 /> 삭제
                            </Button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                  })}
                </ul>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
              <span className="text-[12.5px] text-muted-foreground">
                총 <span className="font-mono tabular-nums text-foreground">{data.total}</span>개 ·
                {' '}페이지 <span className="font-mono tabular-nums">{data.page}</span> / {data.totalPages}
              </span>
              <div className="flex gap-1.5">
                <Button
                  variant="outline" size="sm"
                  disabled={page <= 1}
                  onClick={() => changePage(page - 1)}
                >
                  <ChevronLeft /> 이전
                </Button>
                <Button
                  variant="outline" size="sm"
                  disabled={page >= data.totalPages}
                  onClick={() => changePage(page + 1)}
                >
                  다음 <ChevronRight />
                </Button>
              </div>
            </div>
          </>
          );
        }}
      </QueryBoundary>
    </>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex items-center gap-1.5 h-8 px-3 rounded-[5px] text-[12.5px] font-medium transition-colors',
        active
          ? 'bg-foreground text-background'
          : 'text-muted-foreground hover:text-foreground hover:bg-accent/60',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' });
}

function currentFilterLabel(filter: MessagesFilter): string {
  if (filter === 'unread') return '안 읽음 필터';
  if (filter === 'read') return '읽음 필터';
  return '전체 메시지';
}
